import React from 'react';
import styles from '../irlObservations2026.module.css';
import { canOpenEvidenceFile, Confidence, SuggestHeader, SuggestTabs, SuggestionEdit, stripHtmlText, suggestionCardKey } from './shared';

function hasSuggestedDraft(item) {
    const body = stripHtmlText(item.body);
    const answered = stripHtmlText(item.answered);
    const note = stripHtmlText(item.note);
    return Boolean((body && body !== note) || answered);
}

function responseLayout(item) {
    const kind = String(item.kind || '').trim().toLowerCase();
    const confidence = String(item.confidence || '').trim().toLowerCase();
    if (kind === 'full') return 'full';
    if (kind === 'partial') return 'partial';
    if (confidence === 'high' && hasSuggestedDraft(item)) return 'full';
    if (kind === 'none') return 'none';
    if (confidence === 'high') return 'full';
    if (confidence === 'low') return 'none';
    return 'partial';
}

function suggestedBody(item) {
    const body = stripHtmlText(item.body);
    const answered = stripHtmlText(item.answered);
    const note = stripHtmlText(item.note);
    if (body && body !== note) return body;
    if (answered) return answered;
    return body || note;
}

function SuggestEvidence({ files, onOpen }) {
    if (!files?.length) return null;
    return (
        <div className={styles.suggestEvidence}>
            <div className={styles.suggestEvidenceLabel}>Evidence</div>
            {files.map(file => {
                const meta = [file.name, file.path, file.page].filter(Boolean).join(' · ');
                return (
                    <div key={file.documentId || file.name} className={styles.suggestEvidenceRow}>
                        <span className={styles.suggestEvidenceName}>{meta}</span>
                        <button type="button" className={styles.textLinkStrong} onClick={() => onOpen?.(file)} disabled={!canOpenEvidenceFile(file)}>
                            Open
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

export default function IRL_AI_Review_Review_Suggestions_Responses({
    tab,
    tabs,
    onTab,
    onBack,
    items,
    onOpen,
    onAccept,
    onDismiss,
    onFollowUp,
    onOpenFile,
    counts,
    reviewMeta,
    busy,
}) {
    const [editingId, setEditingId] = React.useState(null);
    const [editText, setEditText] = React.useState('');
    return (
        <>
            <SuggestHeader onBack={onBack} meta={reviewMeta} />
            <SuggestTabs tabs={tabs} tab={tab} onTab={onTab} counts={counts} />
            <div className={styles.detailBody}>
                {items.map(item => {
                    const layout = responseLayout(item);
                    const body = suggestedBody(item);
                    const cardKey = suggestionCardKey(item);
                    const editing = editingId === cardKey;
                    const title = stripHtmlText(item.title);
                    const answered = stripHtmlText(item.answered);
                    const unresolved = stripHtmlText(item.unresolved);
                    const note = stripHtmlText(item.note);
                    return (
                        <div key={cardKey} className={styles.suggestCard}>
                            <div className={styles.suggestTitleRow}>
                                <div>
                                    <div className={styles.suggestId}>{item.id || item.irlId}</div>
                                    <div className={styles.suggestCardTitle}>{title}</div>
                                </div>
                                <Confidence value={item.confidence} />
                            </div>
                            {layout === 'full' && (
                                <div className={styles.suggestedResponse}>
                                    <p className={styles.suggestedResponseLabel}>Suggested response</p>
                                    {editing ? (
                                        <SuggestionEdit
                                            value={editText}
                                            onChange={setEditText}
                                            onSave={() => {
                                                onAccept?.(item, editText);
                                                setEditingId(null);
                                            }}
                                            onCancel={() => setEditingId(null)}
                                            disabled={busy}
                                        />
                                    ) : (
                                        <p className={styles.responseBody}>{body}</p>
                                    )}
                                </div>
                            )}
                            {layout === 'partial' && (
                                <>
                                    <p className={styles.suggestNote}>{note || 'Lens found a partial answer.'}</p>
                                    <div className={styles.splitRow}>
                                        <div className={styles.splitAnswered}>
                                            <p className={styles.splitLabel}>Answered</p>
                                            <p className={styles.responseBody}>{answered}</p>
                                        </div>
                                        <div className={styles.splitUnresolved}>
                                            <p className={styles.splitLabel}>Still unresolved</p>
                                            <p className={styles.responseBody}>{unresolved}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                            {layout === 'none' && (
                                <p className={styles.suggestNoneNote}>
                                    {note || 'Lens did not find sufficient evidence to answer this request.'}
                                </p>
                            )}
                            {layout === 'full' && <SuggestEvidence files={item.evidence} onOpen={onOpenFile} />}
                            <div className={styles.actionRow}>
                                {layout === 'full' && !editing && (
                                    <>
                                        <button type="button" className={styles.primaryBtn} onClick={() => onAccept?.(item)} disabled={busy}>
                                            Accept response
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.mutedBtn}
                                            disabled={busy}
                                            onClick={() => {
                                                setEditingId(cardKey);
                                                setEditText(body);
                                            }}>
                                            Edit & accept
                                        </button>
                                        <button type="button" className={styles.suggestTextAction} onClick={() => onDismiss?.(item)} disabled={busy}>
                                            Keep request open
                                        </button>
                                    </>
                                )}
                                {layout === 'partial' && (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.mutedBtn}
                                            disabled={busy}
                                            onClick={() => onAccept?.(item, item.body || item.answered)}>
                                            Use partial response
                                        </button>
                                        <button type="button" className={styles.suggestTextAction} onClick={() => onFollowUp?.(item)} disabled={busy}>
                                            Create follow-up
                                        </button>
                                    </>
                                )}
                                {layout === 'none' && (
                                    <>
                                        <button type="button" className={styles.suggestTextAction} onClick={() => onFollowUp?.(item)} disabled={busy}>
                                            Create follow-up request
                                        </button>
                                        <button type="button" className={styles.suggestTextAction} onClick={() => onDismiss?.(item)} disabled={busy}>
                                            Keep request open
                                        </button>
                                    </>
                                )}
                                {!editing && (
                                    <button type="button" className={styles.suggestTextActionMuted} onClick={() => onDismiss?.(item)} disabled={busy}>
                                        Dismiss
                                    </button>
                                )}
                                {onOpen && (
                                    <button type="button" className={styles.suggestTextAction} onClick={() => onOpen(item)}>
                                        Open request
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}
